'use client';

import React, { useState, useEffect, useMemo } from 'react';
import ClientsHeader from '@/components/dashboard/clients/ClientsHeader';
import ClientsKpiStrip from '@/components/dashboard/clients/ClientsKpiStrip';
import ClientsFilterBar from '@/components/dashboard/clients/ClientsFilterBar';
import ClientsFilterPills from '@/components/dashboard/clients/ClientsFilterPills';
import ClientsTable, { ClientRecord } from '@/components/dashboard/clients/ClientsTable';
import { ClientDrawer } from '@/components/dashboard/clients/ClientDrawer';
import { ClientDetailDrawer } from '@/components/dashboard/clients/ClientDetailDrawer';

export default function ClientsPage() {
  const [selectedClientForDetail, setSelectedClientForDetail] = useState<ClientRecord | null>(null);
  const [editingClient, setEditingClient] = useState<ClientRecord | null>(null);
  const [isNewDrawerOpen, setIsNewDrawerOpen] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);

  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState<'ALL' | 'ACTIVE' | 'INACTIVE' | 'NEVER'>('ALL');

  // Datos
  const [clients, setClients] = useState<ClientRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 4000);
  };

  // Cargar clientes desde PostgreSQL
  const fetchClients = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/clients');
      const data = await res.json();
      if (data.success) {
        setClients(data.clients || []);
        if (data.clients?.length > 0 && !selectedRowId) {
          setSelectedRowId(data.clients[0].id);
        }
      }
    } catch (err) {
      console.error('Error fetching clients:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  // Métricas de Retención
  const [kpis, setKpis] = useState({
    total: 0,
    newIn30: 0,
    never: 0,
    active: 0,
    inactive: 0
  });

  useEffect(() => {
    const total = clients.length;
    const now = Date.now();
    const thirtyDaysAgo = now - 30 * 24 * 60 * 60 * 1000;

    let newIn30 = 0;
    let never = 0;
    let active = 0;
    let inactive = 0;

    clients.forEach(c => {
      const createdAt = new Date(c.createdAt).getTime();
      if (createdAt >= thirtyDaysAgo) newIn30 += 1;

      if (c.appointmentsCount === 0) {
        never += 1;
      } else {
        active += 1;
      }
    });

    setKpis({
      total,
      newIn30,
      never,
      active,
      inactive
    });
  }, [clients]);

  // Filtrado de clientes
  const filteredClients = useMemo(() => {
    return clients.filter(c => {
      // Buscador
      if (searchTerm.trim()) {
        const q = searchTerm.toLowerCase();
        const matchName = c.name.toLowerCase().includes(q);
        const matchPhone = c.phone.includes(q);
        const matchEmail = c.email?.toLowerCase().includes(q);
        if (!matchName && !matchPhone && !matchEmail) return false;
      }

      // Pastilla de Estado
      if (selectedFilter === 'ACTIVE' && c.appointmentsCount === 0) return false;
      if (selectedFilter === 'NEVER' && c.appointmentsCount > 0) return false;

      return true;
    });
  }, [clients, searchTerm, selectedFilter]);

  // Crear o actualizar cliente
  const handleSaveClient = async (formData: any) => {
    const isEdit = Boolean(formData.id);
    const method = isEdit ? 'PUT' : 'POST';

    const res = await fetch('/api/clients', {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData)
    });
    const data = await res.json();
    if (!data.success) {
      throw new Error(data.error || 'Error guardando cliente');
    }

    showToast(isEdit ? '¡Cliente actualizado con éxito!' : `¡Cliente ${formData.name} registrado con éxito!`);
    await fetchClients();

    // Actualizar cliente seleccionado si estaba abierto
    if (selectedClientForDetail && selectedClientForDetail.id === formData.id) {
      setSelectedClientForDetail({ ...selectedClientForDetail, ...formData });
    }
  };

  // Actualizar notas o campos específicos
  const handleUpdateClientFields = async (clientId: string, updateData: any) => {
    const res = await fetch('/api/clients', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: clientId, ...updateData })
    });
    const data = await res.json();
    if (data.success) {
      showToast('¡Ficha del cliente actualizada!');
      await fetchClients();
      if (selectedClientForDetail && selectedClientForDetail.id === clientId) {
        setSelectedClientForDetail({ ...selectedClientForDetail, ...updateData });
      }
    }
  };

  const handleOpenDetail = (client: ClientRecord) => {
    setSelectedClientForDetail(client);
    setSelectedRowId(client.id);
  };

  const handleOpenSelectedFromBar = () => {
    const found = clients.find(c => c.id === selectedRowId) || clients[0];
    if (found) {
      handleOpenDetail(found);
    }
  };

  const handleOpenEdit = (client: ClientRecord) => {
    setEditingClient(client);
    setIsNewDrawerOpen(true);
  };

  return (
    <div className="space-y-4">
      {/* Toast flotante */}
      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-50 bg-slate-900 text-white px-5 py-3 rounded-2xl shadow-2xl flex items-center gap-3 text-sm font-semibold animate-in slide-in-from-bottom-5 duration-200">
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-400"></span>
          <span>{toastMessage}</span>
        </div>
      )}

      {/* 1. Cabecera con Título, Conteo y Botón de Nuevo Cliente */}
      <ClientsHeader
        clientsCount={filteredClients.length}
        onOpenNewClientModal={() => {
          setEditingClient(null);
          setIsNewDrawerOpen(true);
        }}
      />

      {/* 2. Tira de 4 KPIs de Retención de Clientes */}
      <ClientsKpiStrip
        totalClients={kpis.total}
        newIn30Days={kpis.newIn30}
        neverCame={kpis.never}
        activeCount={kpis.active}
        inactiveCount={kpis.inactive}
      />

      {/* 3. Barra de Herramientas y Filtro de Búsqueda */}
      <ClientsFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onOpenSelectedProfile={handleOpenSelectedFromBar}
        onOpenImport={() => {
          const csvHeader = 'Nombre,Telefono,Email,DNI,Notas\n';
          const csvRows = filteredClients.map(c => `"${c.name}","${c.phone}","${c.email}","${c.dni || ''}","${c.notes || ''}"`).join('\n');
          const blob = new Blob([csvHeader + csvRows], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `clientes_calenddy_${new Date().toISOString().split('T')[0]}.csv`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          showToast('Base de clientes descargada en CSV');
        }}
        onOpenNewClient={() => {
          setEditingClient(null);
          setIsNewDrawerOpen(true);
        }}
        hasSelectedClient={Boolean(selectedRowId)}
      />

      {/* 4. Pastillas de Estado [Todos | Activos | Inactivos | Sin citas] */}
      <ClientsFilterPills
        selectedFilter={selectedFilter}
        onSelectFilter={setSelectedFilter}
        counts={{
          all: kpis.total,
          active: kpis.active,
          inactive: kpis.inactive,
          never: kpis.never
        }}
      />

      {/* 5. Tabla de Clientes con Avatares, Teléfono, WhatsApp y ActionMenu */}
      <ClientsTable
        clients={filteredClients}
        loading={loading}
        selectedClientId={selectedRowId}
        onSelectClient={setSelectedRowId}
        onOpenDetail={handleOpenDetail}
        onEditClient={handleOpenEdit}
      />

      {/* DRAWER LATERAL: Alta y Edición de Cliente (480px) */}
      <ClientDrawer
        isOpen={isNewDrawerOpen}
        onClose={() => {
          setIsNewDrawerOpen(false);
          setEditingClient(null);
        }}
        onSubmit={handleSaveClient}
        initialData={editingClient}
      />

      {/* DRAWER LATERAL: Ficha 360° y Detalle de Cliente (480px) */}
      <ClientDetailDrawer
        client={selectedClientForDetail}
        isOpen={Boolean(selectedClientForDetail)}
        onClose={() => setSelectedClientForDetail(null)}
        onEditClient={handleOpenEdit}
        onUpdateClient={handleUpdateClientFields}
      />
    </div>
  );
}
